import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import changelog from '../data/changelog';

const CATEGORIES = [
  { key: 'nouveautes', label: 'Nouveau', tag: 'bg-emerald-600' },
  { key: 'ameliorations', label: 'Amélioré', tag: 'bg-blue-600' },
  { key: 'corrections', label: 'Corrigé', tag: 'bg-orange-600' },
  { key: 'enCours', label: 'En cours', tag: 'bg-violet-600' },
];

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatMonthYear = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }).toUpperCase();
};

const FilterChip = ({ active, onClick, children, dotClass }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
      active
        ? 'bg-gray-900 text-white border-gray-900'
        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
    }`}
  >
    {dotClass && <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />}
    {children}
  </button>
);

const VersionEntry = ({ entry, isLatest, activeFilter }) => {
  const visibleCategories = CATEGORIES.filter(
    (cat) => (!activeFilter || activeFilter === cat.key) && entry[cat.key]?.length
  );
  if (visibleCategories.length === 0) return null;

  return (
    <section id={`v${entry.version}`} className="scroll-mt-24 flex gap-6 sm:gap-10">
      {/* Rail gauche : numéro de version */}
      <div className="hidden sm:flex flex-col items-end w-28 shrink-0 pt-1">
        <span className="text-[11px] font-semibold text-gray-400 tracking-wider">
          {formatMonthYear(entry.date)}
        </span>
        <span className={`text-2xl font-black tabular-nums ${isLatest ? 'text-gray-900' : 'text-gray-300'}`}>
          v{entry.version}
        </span>
      </div>

      <div className="flex-1 min-w-0 pb-14 border-l-2 border-gray-100 pl-6 sm:pl-8 relative">
        <span
          className={`absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-white ${
            isLatest ? 'bg-gray-900' : 'bg-gray-300'
          }`}
        />

        <div className="sm:hidden mb-2 flex items-center gap-2">
          <span className="text-lg font-black text-gray-900">v{entry.version}</span>
          {isLatest && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-gray-900 text-white px-2 py-0.5 rounded-full">
              Actuelle
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <h2 className="hidden sm:block text-sm font-semibold text-gray-400">
            {formatDate(entry.date)}
          </h2>
          <span className="sm:hidden text-sm text-gray-400">{formatDate(entry.date)}</span>
          {isLatest && (
            <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-wide bg-gray-900 text-white px-2 py-0.5 rounded-full">
              Actuelle
            </span>
          )}
        </div>

        <ul className="space-y-3">
          {visibleCategories.flatMap((cat) =>
            entry[cat.key].map((item, i) => (
              <li key={`${cat.key}-${i}`} className="flex items-start gap-3 text-[15px] text-gray-700 leading-relaxed">
                <span
                  className={`shrink-0 mt-0.5 inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-wide text-white ${cat.tag} rounded px-1.5 py-0.5 w-[74px] text-center`}
                >
                  {cat.label}
                </span>
                <span>{item}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </section>
  );
};

const ChangelogPage = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(null);

  const visibleChangelog = useMemo(
    () => changelog.filter((entry) => !activeFilter || entry[activeFilter]?.length),
    [activeFilter]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Accueil</span>
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-700">Journal des mises à jour</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-4xl font-black text-gray-900 mb-2 tracking-tight">Quoi de neuf</h1>
        <p className="text-gray-500 mb-8">
          Chaque évolution de SkyApp, chantier par chantier.
        </p>

        {/* Filtres par catégorie */}
        <div className="flex flex-wrap gap-2 mb-12">
          <FilterChip active={activeFilter === null} onClick={() => setActiveFilter(null)}>
            Tout
          </FilterChip>
          {CATEGORIES.map((cat) => (
            <FilterChip
              key={cat.key}
              active={activeFilter === cat.key}
              onClick={() => setActiveFilter(activeFilter === cat.key ? null : cat.key)}
              dotClass={cat.tag}
            >
              {cat.label}
            </FilterChip>
          ))}
        </div>

        <div className="space-y-0">
          {visibleChangelog.map((entry) => (
            <VersionEntry
              key={entry.version}
              entry={entry}
              isLatest={changelog[0].version === entry.version}
              activeFilter={activeFilter}
            />
          ))}
        </div>

        <div className="pt-6 border-t border-gray-200 text-sm text-gray-400">
          SkyApp évolue en continu — cette page est mise à jour régulièrement.
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;

